/**
 * Web Audio API utilities: decode blobs → AudioBuffer, generate waveform peaks.
 *
 * Safari-specific hardening:
 *  - Handles the "interrupted" state (unique to WebKit)
 *  - Recreates the AudioContext if it reaches "closed"
 *  - Fires a custom event on recovery so playback can re-schedule
 *  - Plays a silent buffer on first user gesture to fully init the audio session
 *  - Recovers on visibilitychange (safety net for older Safari)
 */

import { capabilities } from '@/capabilities.ts';

let audioCtx: AudioContext | null = null;

/** Listeners notified when the AudioContext recovers from interrupted/suspended. */
const recoveryTarget = new EventTarget();
export const AUDIO_RECOVERED = 'audio-context-recovered';

export function onAudioRecovered(cb: () => void): () => void {
  recoveryTarget.addEventListener(AUDIO_RECOVERED, cb);
  return () => recoveryTarget.removeEventListener(AUDIO_RECOVERED, cb);
}

export function getAudioContext(): AudioContext {
  if (!audioCtx || audioCtx.state === 'closed') {
    audioCtx = new AudioContext();
    // Safari emits statechange when it interrupts/resumes the context
    audioCtx.addEventListener('statechange', onStateChange);
  }
  return audioCtx;
}

/**
 * Return the existing AudioContext WITHOUT creating one.
 * Safari silently mutes AudioContexts created outside a user gesture,
 * so callers that run outside gesture handlers (keepalive timers,
 * visibility listeners) must use this to avoid poisoning the context.
 */
export function getAudioContextIfExists(): AudioContext | null {
  if (audioCtx && audioCtx.state !== 'closed') return audioCtx;
  return null;
}

function onStateChange() {
  if (!audioCtx) return;
  const state = audioCtx.state as string; // Safari adds 'interrupted'

  if (state === 'running') {
    // Recovered from interrupted/suspended — notify listeners
    recoveryTarget.dispatchEvent(new Event(AUDIO_RECOVERED));
  } else if (state === 'suspended') {
    // Attempt to resume — Safari requires this after some transitions
    audioCtx.resume().catch(() => { /* will retry on next user gesture */ });
  }
  // 'interrupted': don't call resume() — Safari rejects it during interruption.
  // Wait for the system to lift it, which fires statechange → 'running' or 'suspended'.
}

/**
 * Resume the AudioContext if it was suspended or interrupted.
 * If the context is closed (unrecoverable), create a fresh one.
 */
export async function ensureAudioContextResumed(): Promise<AudioContext> {
  let ctx = getAudioContext();

  // Safari: 'interrupted' is a string state not in the TS typings
  const state = ctx.state as string;
  const wasNotRunning = state !== 'running';

  if (state === 'suspended' || state === 'interrupted') {
    try {
      await ctx.resume();
    } catch {
      // resume() can reject on Safari if the context is truly dead
      console.warn('[AudioProcessor] resume() failed, creating new AudioContext');
      ctx.removeEventListener('statechange', onStateChange);
      audioCtx = null;
      ctx = getAudioContext();
      await ctx.resume();
    }
  }

  // Play a silent buffer after every resume to force WebKit to reconnect its
  // internal audio hardware pipe. Without this, WebKit (especially in WKWebView)
  // can report 'running' but produce no actual output after a suspension.
  if (wasNotRunning && ctx.state === 'running') {
    playSilentKick(ctx);
  }

  return ctx;
}

/** Play a 1-sample silent buffer to force WebKit's audio hardware connection. */
export function playSilentKick(ctx: AudioContext): void {
  try {
    const buf = ctx.createBuffer(1, 1, ctx.sampleRate);
    const src = ctx.createBufferSource();
    src.buffer = buf;
    src.connect(ctx.destination);
    src.start();
    src.onended = () => { src.disconnect(); };
  } catch { /* non-fatal */ }
}

// ── Visibility / focus recovery ─────────────────────────────────────────
// Resume the AudioContext when the page regains visibility (app switch) or
// focus (WKWebView regaining focus within the Unity window). Both paths
// play a silent kick to force WebKit to reconnect its audio hardware pipe.
// In embedded mode, audio playback is handled by Unity's native audio system,
// so these Safari hardening listeners are unnecessary and could interfere.
const audioListenerAbort = typeof document !== 'undefined' ? new AbortController() : null;
if (!capabilities.embeddedAudio) {
  if (typeof document !== 'undefined' && audioListenerAbort) {
    const signal = audioListenerAbort.signal;

    document.addEventListener('visibilitychange', () => {
      // Use module-level audioCtx directly — never create a new context here.
      // Safari silently mutes contexts created outside a user gesture.
      const ctx = audioCtx;
      if (document.visibilityState === 'visible' && ctx) {
        const state = ctx.state as string;
        if (state === 'suspended') {
          ctx.resume().catch(() => {});
        }
        // Don't resume 'interrupted' — Safari rejects it; wait for system to clear.
      }
    }, { signal });

    // WKWebView embedded in Unity may lose/regain focus without a visibilitychange.
    // The focus event catches this and kicks the AudioContext back to life.
    window.addEventListener('focus', () => {
      const ctx = audioCtx;
      if (ctx) {
        const state = ctx.state as string;
        if (state === 'suspended' || state === 'interrupted') {
          ctx.resume().catch(() => {});
        } else if (state === 'running') {
          // Context reports running but may have lost its hardware pipe — kick it.
          playSilentKick(ctx);
        }
      }
    }, { signal });
  }
}

/** Remove the module-level visibilitychange/focus listeners (for testing / teardown). */
export function cleanupAudioListeners(): void {
  audioListenerAbort?.abort();
}

/**
 * Decode raw audio data (WAV/MP3/OGG bytes) into an AudioBuffer.
 *
 * Uses OfflineAudioContext for decoding — this does NOT require a user gesture,
 * unlike the real AudioContext. This lets us decode audio during project load
 * (e.g. team/share mode) without Safari blocking it.
 */
export async function decodeAudio(rawData: Uint8Array): Promise<AudioBuffer> {
  // Copy to a fresh ArrayBuffer (sql.js may reuse its backing memory)
  const copy = new ArrayBuffer(rawData.byteLength);
  new Uint8Array(copy).set(rawData);
  // OfflineAudioContext params (1ch, 1 frame, 44100Hz) don't affect decoding —
  // decodeAudioData returns the audio's native format regardless.
  const offlineCtx = new OfflineAudioContext(1, 1, 44100);
  return offlineCtx.decodeAudioData(copy);
}

/**
 * Encode an AudioBuffer back to WAV bytes (16-bit PCM).
 * Used as a fallback when raw bytes are missing from the stash.
 */
export function encodeAudioBufferToWav(buffer: AudioBuffer): Uint8Array {
  const numChannels = buffer.numberOfChannels;
  const sampleRate = buffer.sampleRate;
  const bitsPerSample = 16;
  const bytesPerSample = bitsPerSample / 8;
  const blockAlign = numChannels * bytesPerSample;
  const numSamples = buffer.length;
  const dataSize = numSamples * blockAlign;
  const fileSize = 44 + dataSize;

  const out = new ArrayBuffer(fileSize);
  const view = new DataView(out);

  // RIFF header
  writeStr(view, 0, 'RIFF');
  view.setUint32(4, fileSize - 8, true);
  writeStr(view, 8, 'WAVE');
  // fmt chunk
  writeStr(view, 12, 'fmt ');
  view.setUint32(16, 16, true);
  view.setUint16(20, 1, true); // PCM
  view.setUint16(22, numChannels, true);
  view.setUint32(24, sampleRate, true);
  view.setUint32(28, sampleRate * blockAlign, true);
  view.setUint16(32, blockAlign, true);
  view.setUint16(34, bitsPerSample, true);
  // data chunk
  writeStr(view, 36, 'data');
  view.setUint32(40, dataSize, true);

  const channels: Float32Array[] = [];
  for (let ch = 0; ch < numChannels; ch++) channels.push(buffer.getChannelData(ch));

  let off = 44;
  for (let i = 0; i < numSamples; i++) {
    for (let ch = 0; ch < numChannels; ch++) {
      const s = Math.max(-1, Math.min(1, channels[ch][i]));
      view.setInt16(off, s < 0 ? s * 0x8000 : s * 0x7FFF, true);
      off += 2;
    }
  }
  return new Uint8Array(out);
}

function writeStr(view: DataView, offset: number, str: string) {
  for (let i = 0; i < str.length; i++) view.setUint8(offset + i, str.charCodeAt(i));
}

/**
 * Trim leading and trailing silence from an AudioBuffer.
 * Returns a new AudioBuffer with silence removed (keeps a small pad).
 * Returns the original buffer if no significant silence is found.
 */
export function trimSilence(
  buffer: AudioBuffer,
  threshold = 0.005,
  padMs = 30,
): AudioBuffer {
  const data = buffer.getChannelData(0);
  const len = data.length;
  const padSamples = Math.floor((padMs / 1000) * buffer.sampleRate);

  // Find first sample above threshold
  let start = 0;
  for (let i = 0; i < len; i++) {
    if (Math.abs(data[i]) > threshold) {
      start = i;
      break;
    }
  }

  // Find last sample above threshold
  let end = len;
  for (let i = len - 1; i >= start; i--) {
    if (Math.abs(data[i]) > threshold) {
      end = i + 1;
      break;
    }
  }

  // Apply padding (but don't exceed original bounds)
  start = Math.max(0, start - padSamples);
  end = Math.min(len, end + padSamples);

  // Skip trim if less than 50ms would be removed
  const minTrimSamples = Math.floor(0.05 * buffer.sampleRate);
  if (start < minTrimSamples && (len - end) < minTrimSamples) return buffer;

  const newLen = end - start;
  if (newLen <= 0 || newLen >= len) return buffer;

  // Use OfflineAudioContext to create the trimmed buffer — avoids requiring
  // a user gesture just for trimming (trimSilence can be called during TTS decode).
  const offCtx = new OfflineAudioContext(buffer.numberOfChannels, newLen, buffer.sampleRate);
  const trimmed = offCtx.createBuffer(buffer.numberOfChannels, newLen, buffer.sampleRate);
  for (let ch = 0; ch < buffer.numberOfChannels; ch++) {
    const src = buffer.getChannelData(ch);
    trimmed.getChannelData(ch).set(src.subarray(start, end));
  }
  return trimmed;
}

/**
 * Downsample an AudioBuffer to a fixed number of peak amplitude values
 * for waveform rendering. Uses max absolute amplitude per bucket.
 */
export function generatePeaks(buffer: AudioBuffer, targetSamples = 2000): Float32Array {
  const channelData = buffer.getChannelData(0); // use first channel
  const totalSamples = channelData.length;
  const samplesPerPeak = Math.max(1, Math.floor(totalSamples / targetSamples));
  const peakCount = Math.ceil(totalSamples / samplesPerPeak);
  const peaks = new Float32Array(peakCount);

  for (let i = 0; i < peakCount; i++) {
    const start = i * samplesPerPeak;
    const end = Math.min(start + samplesPerPeak, totalSamples);
    let max = 0;
    for (let j = start; j < end; j++) {
      const abs = Math.abs(channelData[j]);
      if (abs > max) max = abs;
    }
    peaks[i] = max;
  }

  return peaks;
}
